export type AiProviderMode = 'inherit' | 'direct' | 'disabled';
export type AiConfigurationSource = 'platform' | 'workspace' | 'disabled';
export const AI_KNOWLEDGE_DOMAINS = [
  'projects',
  'finance',
  'procurement',
  'workforce',
  'safety',
  'documents',
] as const;
export type AiKnowledgeDomain = (typeof AI_KNOWLEDGE_DOMAINS)[number];
export interface AiAnthropicConfiguration {
  mode: AiProviderMode;
  model: string;
}
export interface AiOpenAiConfiguration {
  mode: AiProviderMode;
  model: string;
  imageModel: string;
  transcriptionModel: string;
  speechModel: string;
}
interface ProviderMetadata {
  keyPresent: boolean;
  configured: boolean;
  source: AiConfigurationSource;
  workspaceKeyPresent?: boolean;
  workspaceCredentialReadable?: boolean | null;
}
export interface AiConfigurationResponse {
  version: number;
  updatedAt: string | null;
  canManage: boolean;
  providers: {
    anthropic: AiAnthropicConfiguration & ProviderMetadata;
    openai: AiOpenAiConfiguration & ProviderMetadata;
  };
  credentialStorage: { configured: boolean; detail: string };
  saveResult?: {
    code: 'AI_CONFIGURATION_SAVED' | 'AI_CONFIGURATION_UNCHANGED';
    savedVersion: number;
  };
}
export interface AiConfigurationInput {
  expectedVersion: number;
  providers: {
    anthropic?: Partial<AiAnthropicConfiguration> & { apiKey?: string; removeKey?: boolean };
    openai?: Partial<AiOpenAiConfiguration> & { apiKey?: string; removeKey?: boolean };
  };
}
export interface AiAssistantPreferences {
  voiceOutputEnabled: boolean;
  assistantVoice: string;
  speechRate: number;
  voicePitch: number;
  voiceVolume: number;
  voiceLanguage: string;
  continuousListening: boolean;
  voiceAutoSend: boolean;
  proactiveNarration: boolean;
  aiVoice: string;
  responseStyle: 'focused' | 'balanced' | 'creative';
  temperature: number;
  actionMode: 'draft_only' | 'approval';
  opportunityAlerts: boolean;
  knowledgeSources: Record<AiKnowledgeDomain, boolean>;
}
export const DEFAULT_AI_PREFERENCES: AiAssistantPreferences = {
  voiceOutputEnabled: false,
  assistantVoice: '',
  speechRate: 1,
  voicePitch: 1,
  voiceVolume: 1,
  voiceLanguage: 'en-US',
  continuousListening: false,
  voiceAutoSend: false,
  proactiveNarration: false,
  aiVoice: 'alloy',
  responseStyle: 'balanced',
  temperature: 0.3,
  actionMode: 'approval',
  opportunityAlerts: true,
  knowledgeSources: {
    projects: true,
    finance: true,
    procurement: true,
    workforce: true,
    safety: true,
    documents: true,
  },
};
export interface AiPreferencesResponse {
  version: number;
  updatedAt: string | null;
  preferences: AiAssistantPreferences;
}
export interface AiPreferencesInput {
  expectedVersion: number;
  preferences: Partial<AiAssistantPreferences>;
}
export interface AiKnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  scope: 'all' | 'helios';
  createdAt: string;
  updatedAt: string;
}
export interface AiKnowledgeResponse {
  version: number;
  entries: AiKnowledgeEntry[];
  canManage: boolean;
}
export type AiKnowledgeInput = Pick<
  AiKnowledgeEntry,
  'title' | 'content' | 'category' | 'scope'
> & { expectedVersion: number };
export interface AiGuardrailPolicy {
  operatingInstructions: string;
  financialSafety: string;
  dataPrivacy: string;
  escalationPolicy: string;
  evidenceStandards: string;
}
export interface AiGuardrailRule {
  id: string;
  type: 'restriction' | 'permission' | 'tone' | 'knowledge';
  description: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface AiGuardrailsResponse {
  version: number;
  policy: AiGuardrailPolicy;
  rules: AiGuardrailRule[];
  canManage: boolean;
  enforcedPolicy: string;
}
export interface AiGuardrailPolicyInput {
  expectedVersion: number;
  policy: Partial<AiGuardrailPolicy>;
}
export type AiGuardrailRuleInput = Pick<AiGuardrailRule, 'type' | 'description' | 'active'> & {
  expectedVersion: number;
};
export type AiGuardrailRuleUpdate = Partial<
  Pick<AiGuardrailRule, 'type' | 'description' | 'active'>
> & { id: string; expectedVersion: number };
export interface AiDeleteInput {
  id: string;
  expectedVersion: number;
}
export interface AiConnectionTestInput {
  provider: 'anthropic' | 'openai';
  expectedVersion: number;
}
export interface AiConnectionTestResult {
  provider: 'anthropic' | 'openai';
  configured: boolean;
  verified: boolean;
  checkedAt: string;
  detail: string;
  checkKind?: 'authentication';
  generationTested?: false;
  failureCode?: string;
  httpStatus?: number;
}
